/**
 * The Life Areas, as the backend defines them.
 *
 * Every area — its key, order, title, subtitle, description, colour and icon — is
 * declared once, in aris-core's Life Area registry, and served by
 * `GET /ARIS/v4/explore/life-areas`. Nothing here lists the areas: adding,
 * renaming or retiring one is a backend change that this app picks up on the
 * next load. The only per-area thing kept here is the SVG for each `icon`
 * (LifeAreaIcon.jsx); an unknown icon gets a generic glyph.
 *
 * Areas are cached per page load. Components that render an area from its key
 * alone call `useLifeAreas()` once so they re-render when the list arrives.
 */
import { useEffect, useState } from "react";

export const FALLBACK_COLOR = "#98a2b3";
export const FALLBACK_ICON = "bulb";

const registry = new Map();
const listeners = new Set();
let pending = null;

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

/** Store areas the backend sent (the life-areas list). */
export const rememberLifeAreas = (items) => {
  if (!Array.isArray(items)) return;
  let changed = false;
  items.forEach((item) => {
    if (item?.key && item?.title) {
      registry.set(item.key, item);
      changed = true;
    }
  });
  if (changed) listeners.forEach((notify) => notify());
};

/** Fetch the areas once per page load; a failure is retried on the next call. */
export const loadLifeAreas = () => {
  if (!pending) {
    // Imported lazily so modules that only resolve areas don't pull in the API client.
    pending = import("../../Services/explore")
      .then(({ getLifeAreas }) => getLifeAreas())
      .then((areas) => rememberLifeAreas(areas))
      .catch(() => {
        pending = null;
      });
  }
  return pending;
};

/** The areas known so far, in display order. */
export const knownLifeAreas = () => [...registry.values()].sort(byOrder);

/** Loads the areas and re-renders the caller when they arrive. */
export const useLifeAreas = () => {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const notify = () => setVersion((v) => v + 1);
    listeners.add(notify);
    loadLifeAreas();
    return () => listeners.delete(notify);
  }, []);
  return knownLifeAreas();
};

/**
 * An area ready to render: the item's own fields first, then the cached
 * definition, then a neutral tile. An unknown key still renders, with its raw
 * key, rather than vanishing.
 */
export const resolveLifeArea = (item) => {
  const key = item?.key || item?.display_area_key || "";
  const known = registry.get(key) || {};
  return {
    key,
    title: item?.title || known.title || key.replace(/_/g, " "),
    subtitle: item?.subtitle || known.subtitle || "",
    description: item?.description || known.description || "",
    color: item?.color || known.color || FALLBACK_COLOR,
    icon: item?.icon || known.icon || FALLBACK_ICON,
    // Plan 49: product's approved icon and the area's header artwork (public URLs).
    iconUrl: item?.icon_image_url || known.icon_image_url || "",
    headerUrl: item?.header_image_url || known.header_image_url || "",
  };
};

/** Test helper: forget every cached area. */
export const __resetLifeAreasForTests = () => {
  registry.clear();
  pending = null;
};
